import React, { useId, useState } from 'react';

import classNames from 'classnames';

import type { CheckboxProps } from '../Checkbox/Checkbox';
import type { FormFieldTagProps } from '../FormFieldTag';
import type { FormLayoutProps } from '../FormLayout';
import type { InputProps } from '../Input/Input';
import type { RadioButtonProps } from '../RadioButton/RadioButton';
import type { SelectProps } from '../Select';
import type { SliderProps } from '../Slider';
import type { TextareaProps } from '../Textarea';

import { AnalyticsId, FormOnColor, FormSize } from '../../constants';
import { useIdWithFallback } from '../../hooks/useIdWithFallback';
import { isComponent } from '../../utils/component';
import Checkbox from '../Checkbox/Checkbox';
import ErrorWrapper from '../ErrorWrapper';
import FormFieldTag from '../FormFieldTag';
import FormLayout from '../FormLayout';
import Input from '../Input/Input';
import RadioButton from '../RadioButton/RadioButton';
import { getRadioLabelClasses } from '../RadioButton/utils';
import Select from '../Select';
import Slider from '../Slider';
import Textarea from '../Textarea';
import Title, { type TitleTags } from '../Title';

import formGroupStyles from './styles.module.scss';

export type FormGroupTags = 'fieldset' | 'div';

export interface FormGroupProps {
  /** Can be used as a replacement text for legend in cases where the group title already exists externally */
  ariaLabelledBy?: string;
  /** title for the the fieldset */
  title?: string;
  /** text placed in the legend tag of the fieldset */
  legend?: string;
  /** id for the legend tag */
  legendId?: string;
  /** Items in the FormGroup component */
  children?: React.ReactNode;
  /** Adds custom classes to the element. */
  className?: string;
  /** Adds custom classes to the errorWrapper. */
  errorWrapperClassName?: string;
  /** Adds custom classes to the fieldset element. */
  fieldsetClassName?: string;
  /** Sets a tag that describes whether the form group is required or optional */
  formFieldTag?: React.ReactNode;
  /** Adds custom classes to the legend element. */
  legendClassName?: string;
  /** Changes the visuals of the formgroup */
  onColor?: keyof typeof FormOnColor;
  /** Changes the visuals of the formgroup */
  size?: keyof typeof FormSize;
  /** Error message */
  error?: string;
  /** Error text id */
  errorTextId?: string;
  /** Sets the data-testid attribute. */
  testId?: string;
  /** Sets the data-testid attribute for the error-wrapper. */
  errorWrapperTestId?: string;
  /** Unique name for the child input element */
  name?: string;
  /** Unique name for the fieldset */
  fieldsetName?: string;
  /** Sets div instead of fieldset tag */
  htmlMarkup?: FormGroupTags;
  /** Markup for legend if formgroup htmlMarkup is div*/
  legendHtmlMarkup?: TitleTags;
  /** Markup for title */
  titleHtmlMarkup?: TitleTags;
  /** Renders the error component (Default: true) */
  renderError?: boolean;
  /** Ref passed to the error message element */
  errorMessageRef?: React.Ref<HTMLDivElement | null>;
}

export const FormGroup: React.FC<FormGroupProps> = (props: FormGroupProps) => {
  const {
    ariaLabelledBy,
    className,
    errorTextId: errorTextIdProp,
    fieldsetClassName,
    formFieldTag,
    legendClassName,
    legendId,
    onColor = FormOnColor.onwhite,
    size = FormSize.medium,
    error,
    name,
    htmlMarkup = 'fieldset',
    renderError = true,
    errorWrapperClassName,
    errorWrapperTestId,
    errorMessageRef,
    legendHtmlMarkup = 'h5',
    titleHtmlMarkup = 'h4',
  } = props;
  const [checkedRadioId, setCheckedRadioId] = useState<string>();
  const radioGroupId = useId();
  const errorTextId = useIdWithFallback(errorTextIdProp);
  const onDark = onColor === FormOnColor.ondark;
  const isLarge = size === FormSize.large;
  const formGroupWrapperClasses = classNames(formGroupStyles['form-group-wrapper'], className);
  const titleClasses = classNames({
    [formGroupStyles['form-group-wrapper__title--on-dark']]: onDark && !error,
  });

  const CustomTagForLegend = legendHtmlMarkup;

  const legendClasses = classNames(
    formGroupStyles['field-set__legend'],
    {
      [formGroupStyles['field-set__legend--on-dark']]: onDark && !error,
    },
    legendClassName
  );

  const fieldsetClasses = classNames(formGroupStyles['field-set'], fieldsetClassName);

  const mapFormComponent = (child: React.ReactNode, index: number): React.ReactNode => {
    if (isComponent<FormLayoutProps>(child, FormLayout)) {
      return React.cloneElement(child, {
        size,
        mapHelper: mapFormComponent,
      });
    } else if (isComponent<FormGroupProps>(child, FormGroup)) {
      return React.cloneElement(child, {
        onColor,
        size,
        error,
        renderError: false,
        errorTextId: errorTextId,
      });
    } else if (isComponent<CheckboxProps>(child, Checkbox)) {
      return React.cloneElement(child, {
        name: name ?? child.props.name,
        onColor,
        size,
        error: !!error,
        errorTextId: errorTextId,
      });
    } else if (isComponent<RadioButtonProps>(child, RadioButton)) {
      const radioId = typeof child.props.inputId === 'undefined' ? radioGroupId + index : child.props.inputId;
      return React.cloneElement(child, {
        inputId: radioId,
        name: name ?? child.props.name,
        onColor,
        size,
        onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
          setCheckedRadioId(event.target.id);
          if (child.props.onChange) child.props.onChange(event);
        },
        error: !!error,
        errorTextId: errorTextId,
        labelClassNames: getRadioLabelClasses(radioId, onColor as FormOnColor, isLarge, checkedRadioId),
      });
    } else if (isComponent<InputProps>(child, Input)) {
      return React.cloneElement(child, {
        name: name ?? child.props.name,
        onColor,
        size,
        error: !!error,
        errorTextId: errorTextId,
      });
    } else if (isComponent<TextareaProps>(child, Textarea)) {
      return React.cloneElement(child, {
        name: name ?? child.props.name,
        onColor,
        error: !!error,
        errorTextId: errorTextId,
      });
    } else if (isComponent<SelectProps>(child, Select)) {
      return React.cloneElement(child, {
        name: name ?? child.props.name,
        onColor,
        error: !!error,
        errorTextId: errorTextId,
      });
    } else if (isComponent<SliderProps>(child, Slider)) {
      return React.cloneElement(child, {
        name: name ?? child.props.name,
        error: !!error,
        errorTextId: errorTextId,
      });
    }
    return child;
  };

  const formGroupContent = (): React.ReactNode => {
    return (
      <div>
        {htmlMarkup === 'div' && (
          <div className={fieldsetClasses}>
            {props.legend && (
              <>
                <CustomTagForLegend id={legendId} className={legendClasses}>
                  {props.legend}
                  {formFieldTag && isComponent<FormFieldTagProps>(formFieldTag, FormFieldTag) && React.cloneElement(formFieldTag)}
                </CustomTagForLegend>
              </>
            )}
            {React.Children.map(props.children, mapFormComponent)}
          </div>
        )}
        {htmlMarkup === 'fieldset' && (
          <fieldset aria-labelledby={ariaLabelledBy} name={props.fieldsetName} className={fieldsetClasses}>
            {props.legend && (
              <>
                <legend id={legendId} className={legendClasses}>
                  {props.legend}
                  {formFieldTag && isComponent<FormFieldTagProps>(formFieldTag, FormFieldTag) && React.cloneElement(formFieldTag)}
                </legend>
              </>
            )}
            {React.Children.map(props.children, mapFormComponent)}
          </fieldset>
        )}
      </div>
    );
  };

  return (
    <div data-testid={props.testId} data-analyticsid={AnalyticsId.FormGroup} className={formGroupWrapperClasses}>
      {props.title && (
        <Title
          className={titleClasses}
          htmlMarkup={titleHtmlMarkup}
          appearance={'title4'}
          margin={{ marginTop: 0, marginBottom: error ? 1 : 2 }}
        >
          {props.title}
        </Title>
      )}
      <ErrorWrapper
        className={errorWrapperClassName}
        errorText={error}
        testId={errorWrapperTestId}
        errorTextId={errorTextId}
        errorMessageRef={errorMessageRef}
        renderError={renderError}
      >
        {formGroupContent()}
      </ErrorWrapper>
    </div>
  );
};

FormGroup.displayName = 'FormGroup';

export default FormGroup;
